/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type AccessRole,
  saveSelectedProfile,
  loadSelectedProfile,
  clearSelectedProfile,
} from '../../domain/entities/AccessRole';
import { employeeKeys } from '@/modules/employees/application/employeeKeys';
import { getTimelogRepository } from '@/modules/attendance/infrastructure/repositories/container';
import { TimelogRefreshScheduler } from '@/modules/attendance/infrastructure/sync/TimelogRefreshScheduler';
import { EmployeeRefreshScheduler } from '@/modules/employees/infrastructure/sync/EmployeeRefreshScheduler';
import type { CompositeTimelogRepository } from '@/modules/attendance/infrastructure/repositories/CompositeTimelogRepository';

interface ProfileContextValue {
  selectedRole: AccessRole | null;
  selectRole: (role: AccessRole) => void;
  clearProfile: () => void;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState<AccessRole | null>(() =>
    loadSelectedProfile(),
  );

  const selectRole = useCallback((role: AccessRole) => {
    saveSelectedProfile(role);
    setSelectedRole(role);
    void queryClient.invalidateQueries({ queryKey: employeeKeys.byAccessRole(role.idAccessRole) });
  }, [queryClient]);

  const clearProfile = useCallback(() => {
    clearSelectedProfile();
    setSelectedRole(null);
  }, []);

  // Slice E (profile-auto-selection R5): listen for the global auth-logout
  // event so that any logout path (admin pages, expired session, picker)
  // also resets the in-memory selected role. Storage is already cleared by
  // AuthContext.clearAuthSession(); this keeps the React state in sync so
  // the next visit to /select-profile re-evaluates auto-select correctly.
  useEffect(() => {
    const handler = () => { setSelectedRole(null); };
    window.addEventListener('mi:auth-logout', handler);
    return () => { window.removeEventListener('mi:auth-logout', handler); };
  }, []);

  // T2.10: Pre-fetch last month's timelogs when profile is selected
  // T2.12: Start background refresh scheduler
  useEffect(() => {
    if (!selectedRole) return;

    // T2.10: Pre-fetch timelogs in background (non-blocking)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        
        const startDate = oneMonthAgo.toISOString().split('T')[0]!;
        const endDate = new Date().toISOString().split('T')[0]!;
        
        const repo = getTimelogRepository() as CompositeTimelogRepository;
        repo.fetchAllWithFallback({
          accessRoleId: selectedRole.idAccessRole,
          startDate,
          endDate,
        }).catch((err) => {
          console.warn('[ProfileContext] Timelog pre-fetch failed:', err);
        });
      });
    }

    // T2.12: Start background refresh scheduler (5-minute interval)
    const timelogScheduler = TimelogRefreshScheduler.getInstance();
    timelogScheduler.start(selectedRole.idAccessRole, queryClient);

    // Employee + face-embedding pull scheduler (1-minute interval).
    // Refreshes the employees list from the backend so that enrolments done
    // on other terminals propagate to this one within a minute. Also fires
    // an immediate refresh when the browser reports `online`, so recovering
    // from an offline period does not wait for the next tick.
    const employeeScheduler = EmployeeRefreshScheduler.getInstance();
    employeeScheduler.start(selectedRole.idAccessRole, queryClient);

    return () => {
      timelogScheduler.stop();
      employeeScheduler.stop();
    };
  }, [selectedRole, queryClient]);

  return (
    <ProfileContext.Provider value={{ selectedRole, selectRole, clearProfile }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used inside ProfileProvider');
  return ctx;
}
